import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Filter, X } from 'lucide-react';
import {
  DEPARTMENT_DISPLAY_LABELS,
  PROJECT_PHASE_LABELS,
  type ProjectDepartment,
  type ProjectPhase,
  type TaskStatus,
} from '../../types';
import { GROUP_TIMELINE_STATUS_LABELS } from '../../utils/groupTimeline';
import {
  countActiveGroupTimelineFilters,
  EMPTY_GROUP_TIMELINE_FILTER,
  toggleFilterValue,
  type GroupTimelineDetailFilter,
  type GroupTimelineFilterProjectOption,
  type GroupTimelineFilterSectionOption,
  type GroupTimelineFilterSupplierOption,
  type GroupTimelineSimulatedFilter,
} from '../../utils/groupTimelineFilter';
import { placeGroupTimelineFilterMenu } from '../../utils/groupTimelineFilterMenu';
import './GroupTimelineColorControls.css';

const PHASE_OPTIONS: ProjectPhase[] = ['estimating', 'preconstruction', 'contracted'];
const STATUS_OPTIONS: TaskStatus[] = ['pending', 'in-progress', 'review', 'completed', 'blocked', 'on-hold'];
const DEPARTMENT_OPTIONS: ProjectDepartment[] = [
  'preconstruction',
  'design',
  'procurement',
  'production',
  'installation',
];
const SIMULATED_OPTIONS: Array<{ id: GroupTimelineSimulatedFilter; label: string }> = [
  { id: 'all', label: 'All jobs' },
  { id: 'simulated', label: 'Simulated only' },
  { id: 'real', label: 'Hide simulated' },
];

function placeMenuFromTrigger(trigger: HTMLElement, menu?: HTMLElement | null) {
  return placeGroupTimelineFilterMenu(
    trigger.getBoundingClientRect(),
    { width: window.innerWidth, height: window.innerHeight },
    menu?.offsetHeight,
  );
}

interface GroupTimelineFilterControlsProps {
  filter: GroupTimelineDetailFilter;
  onChange: (next: GroupTimelineDetailFilter) => void;
  projects: GroupTimelineFilterProjectOption[];
  sections: GroupTimelineFilterSectionOption[];
  suppliers: GroupTimelineFilterSupplierOption[];
  /** Shown on the trigger, for example "C". */
  clearShortcut?: string;
}

const GroupTimelineFilterControls: React.FC<GroupTimelineFilterControlsProps> = ({
  filter,
  onChange,
  projects,
  sections,
  suppliers,
  clearShortcut,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const activeCount = countActiveGroupTimelineFilters(filter);

  const placeMenu = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setMenuPos(placeMenuFromTrigger(trigger, menuRef.current));
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) return;
    const next = placeMenuFromTrigger(triggerRef.current, menuRef.current);
    setMenuPos((prev) => (
      prev
      && prev.top === next.top
      && prev.left === next.left
      && prev.width === next.width
      && prev.maxHeight === next.maxHeight
        ? prev
        : next
    ));
  }, [open, query, filter, projects.length, sections.length, suppliers.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const visibleProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.label.toLowerCase().includes(q));
  }, [projects, query]);

  const toggleList = <T extends string>(key: keyof GroupTimelineDetailFilter, value: T) => {
    const current = filter[key];
    if (!Array.isArray(current)) return;
    onChange({ ...filter, [key]: toggleFilterValue(current as T[], value) });
  };

  return (
    <div className="pm-hub-color-controls" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`pm-hub-color-trigger${open || activeCount ? ' is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={activeCount ? `Filter timeline, ${activeCount} active` : 'Filter timeline'}
        aria-keyshortcuts={clearShortcut}
        title={clearShortcut ? `Filter projects, groups, phase, status, and suppliers. ${clearShortcut} clears filters` : 'Filter projects, groups, phase, status, and suppliers'}
        onClick={() => setOpen((v) => !v)}
      >
        <Filter size={13} strokeWidth={2} aria-hidden />
        <span>Filter{activeCount ? ` · ${activeCount}` : ''}</span>
        {clearShortcut ? (
          <span className="pm-view-btn-shortcut-tip" role="tooltip">
            Clear: <kbd>{clearShortcut}</kbd>
          </span>
        ) : null}
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          className="pm-dropdown pm-hub-color-menu pm-hub-gt-filter-menu"
          role="dialog"
          aria-label="Timeline filters"
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxWidth: menuPos.width,
            maxHeight: menuPos.maxHeight,
            right: 'auto',
          }}
        >
          <div className="pm-hub-gt-filter-head">
            <span>Filters</span>
            {activeCount > 0 && (
              <button
                type="button"
                className="pm-hub-gt-filter-clear"
                title={clearShortcut ? `Clear filters (${clearShortcut})` : 'Clear filters'}
                onClick={() => onChange({ ...EMPTY_GROUP_TIMELINE_FILTER })}
              >
                <X size={11} strokeWidth={2.5} aria-hidden />
                Clear
              </button>
            )}
          </div>

          {filter.taskIds.length > 0 && (
            <p className="pm-hub-gt-filter-conflict">
              Showing {filter.taskIds.length} conflicting {filter.taskIds.length === 1 ? 'task' : 'tasks'}
            </p>
          )}

          <p className="pm-hub-gt-filter-label">Projects</p>
          {projects.length > 4 && (
            <input
              className="pm-hub-gt-filter-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects"
              aria-label="Search project list"
            />
          )}
          <div className="pm-hub-gt-filter-list">
            {visibleProjects.length === 0 && (
              <p className="pm-hub-gt-filter-empty">{query ? 'No matching projects.' : 'No projects.'}</p>
            )}
            {visibleProjects.map((p) => {
              const on = filter.projectIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`pm-hub-gt-filter-check${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleList('projectIds', p.id)}
                >
                  <span className="pm-hub-gt-filter-box" aria-hidden>
                    {on ? <Check size={11} strokeWidth={2.5} /> : null}
                  </span>
                  <span>{p.label}</span>
                </button>
              );
            })}
          </div>

          <p className="pm-hub-gt-filter-label">Groups</p>
          <div className="pm-hub-gt-filter-chips">
            {sections.map((s) => {
              const on = filter.sectionIds.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`pm-hub-gt-filter-chip${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleList('sectionIds', s.id)}
                >
                  {s.name}
                </button>
              );
            })}
          </div>

          <p className="pm-hub-gt-filter-label">Phase</p>
          <div className="pm-hub-gt-filter-chips">
            {PHASE_OPTIONS.map((phase) => {
              const on = filter.phases.includes(phase);
              return (
                <button
                  key={phase}
                  type="button"
                  className={`pm-hub-gt-filter-chip${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleList('phases', phase)}
                >
                  {PROJECT_PHASE_LABELS[phase]}
                </button>
              );
            })}
          </div>

          <p className="pm-hub-gt-filter-label">Task status</p>
          <div className="pm-hub-gt-filter-chips">
            {STATUS_OPTIONS.map((status) => {
              const on = filter.statuses.includes(status);
              return (
                <button
                  key={status}
                  type="button"
                  className={`pm-hub-gt-filter-chip${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleList('statuses', status)}
                >
                  {GROUP_TIMELINE_STATUS_LABELS[status]}
                </button>
              );
            })}
          </div>

          <p className="pm-hub-gt-filter-label">Department</p>
          <div className="pm-hub-gt-filter-chips">
            {DEPARTMENT_OPTIONS.map((dept) => {
              const on = filter.departments.includes(dept);
              return (
                <button
                  key={dept}
                  type="button"
                  className={`pm-hub-gt-filter-chip${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleList('departments', dept)}
                >
                  {DEPARTMENT_DISPLAY_LABELS[dept]}
                </button>
              );
            })}
          </div>

          {suppliers.length > 0 && (
            <>
              <p className="pm-hub-gt-filter-label">Supplier</p>
              <div className="pm-hub-gt-filter-chips">
                {suppliers.map((s) => {
                  const on = filter.supplierOrgIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`pm-hub-gt-filter-chip${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleList('supplierOrgIds', s.id)}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <p className="pm-hub-gt-filter-label">Simulated</p>
          <div className="pm-hub-gt-filter-chips">
            {SIMULATED_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`pm-hub-gt-filter-chip${filter.simulated === opt.id ? ' is-on' : ''}`}
                aria-pressed={filter.simulated === opt.id}
                onClick={() => onChange({ ...filter, simulated: opt.id })}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default GroupTimelineFilterControls;
